import {useEffect, useState, createContext, useContext} from 'react';

export type CarbonLookupItem = {
    Id: number;
    Name: string;
};

export type CarbonLookups = {
    ProjectStandards: CarbonLookupItem[];
    CreditTypes: CarbonLookupItem[];
};

const emptyLookups: CarbonLookups = {ProjectStandards: [], CreditTypes: []};

// Module-level copy so non-component code (filters, formatters) can read lookups synchronously
let cachedLookups: CarbonLookups = emptyLookups;

const CarbonLookupsContext = createContext<CarbonLookups>(emptyLookups);

export function CarbonLookupsProvider({children}: {children: React.ReactNode}) {
    const [lookups, setLookups] = useState<CarbonLookups>(null);

    const getLookups = async () => {
        let result = emptyLookups;
        try {
            const response = await fetch('/api/carbon-lookups');
            if (response.ok) {
                const data = await response.json();
                result = {
                    ProjectStandards: data?.ProjectStandards ?? [],
                    CreditTypes: data?.CreditTypes ?? []
                };
            }
        } catch (e) {
            console.error('Failed to load carbon lookups', e);
        }
        cachedLookups = result;
        setLookups(result);
    };

    useEffect(() => {
        getLookups();
    }, []);

    if (!lookups) return null;

    return <CarbonLookupsContext.Provider value={lookups}>{children}</CarbonLookupsContext.Provider>;
}

export function useCarbonLookups(): CarbonLookups {
    return useContext(CarbonLookupsContext);
}

export function getProjectStandardName(id: number): string {
    return cachedLookups.ProjectStandards.find((x) => x.Id === id)?.Name ?? '';
}

export function getCreditTypeName(id: number): string {
    return cachedLookups.CreditTypes.find((x) => x.Id === id)?.Name ?? '';
}
